import React, { useEffect } from 'react';
import {
  Form,
  Input,
  Option,
  RequiredHelpPhrase,
  Row,
  Select,
  SelectParam,
  SonForm,
  SubmitButton,
  TextParam,
  useForm,
} from '../../../_lib/Form2';
import { useRefreshBlock } from '../../../_lib/BlockFetcher';
import { ContentParagraph } from '../../../_lib/components/ContentParagraph';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { asFetchableView } from '../../../_lib/ViewFetcher';
import { ContentVideoLayout } from '../VideoBlock.types';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';

type VideoUploadForm = {
  form: SonForm<
    | TextParam<'title'>
    | TextParam<'source'>
    | TextParam<'copyright'>
    | TextParam<'caption'>
    | TextParam<'captionsFile'>
    | SelectParam<'layout', { value: ContentVideoLayout }>
  >;
  optionalDescription?: string;
};

export function VideoUpload({ form, optionalDescription }: VideoUploadForm) {
  const { t } = useSonTranslation('VideoUpload');
  const { formProps, params, fetchState } = useForm({ form, activateLeavingGuard: true });
  const { delayedRefresh } = useRefreshBlock();
  const selectLayoutParam = params.getByName('layout');
  const uId = useHtmlValidId();

  useEffect(() => {
    if (fetchState.type === 'success') {
      delayedRefresh();
    }
  }, [delayedRefresh, fetchState.type]);

  return (
    <div className="video__upload">
      <div className="video__upload-description">
        <ContentParagraph>
          {/* keep whitespace here! */}
          {t(`supportedVideoFormats`)} <abbr title={t(`mp4AbbrTitle`)}>{t('mp4')}</abbr>.
        </ContentParagraph>
        {optionalDescription && optionalDescription !== '' && (
          <ContentParagraph>{optionalDescription}</ContentParagraph>
        )}
      </div>
      <RequiredHelpPhrase />
      <Form {...formProps} id={`video-update-form-${uId}`} modifier="no-padding">
        <Row>
          <Input label={t('title')} param={params.getByName('title')} />
        </Row>
        <Row>
          <Input label={t('source')} helpText={t('sourceHelpText')} param={params.getByName('source')} />
        </Row>
        <Row>
          <Input label={t('caption')} param={params.getByName('caption')} />
        </Row>
        <Row>
          <Select label={t('layout')} param={selectLayoutParam}>
            {selectLayoutParam.options.map((option: { value: ContentVideoLayout }) => (
              <Option key={option.value} value={option.value}>
                {t(`inputs.layout.options.${option.value}`)}
              </Option>
            ))}
          </Select>
        </Row>
        <Row>
          <Input
            label={t('captionsFile')}
            helpText={t('captionsFileHelpText')}
            param={params.getByName('captionsFile')}
          />
        </Row>
        <Row>
          <Input label={t('copyright')} helpText={t('videoCopyrightHelpText')} param={params.getByName('copyright')} />
        </Row>
        <SubmitButton
          buttonLabel={t('save')}
          messageSuccess={t('uploadSuccess')}
          messageSending={t(`uploadInProgress`)}
          messageFail={t('uploadFail.general')}
          fetchState={fetchState}
        />
      </Form>
    </div>
  );
}
export const VideoUploadEditView = asFetchableView(VideoUpload);
